import { EAppTemplateType } from "templates-common-library/baseClasses/CompatibilityChecker";
import { IDocument, LayoutTheme } from "../../interfaces/interfaces";

import ResizeObserver from "resize-observer-polyfill";

export function getTheme(darkModeEnabled: boolean): LayoutTheme {
  return darkModeEnabled ? "dark" : "light";
}

// getBaseUrl
export function getBaseUrl(portal?: __esri.Portal): string {
  if (!portal) {
    return;
  }
  const { customBaseUrl, portalHostname, urlKey } = portal;
  const { protocol } = location;
  const url = urlKey ? `${urlKey}.${customBaseUrl}` : portalHostname;
  return `${protocol}//${url}`;
}

export function logInvalidProperty(props: string[]): void {
  console.error(
    `Invalid property name - please check Configuration Parameters JSON: ${props.join(
      " > "
    )}`
  );
}

export function strHasSpecialCharsOrNum(val: string): boolean {
  if (val === undefined || val === null) {
    return false;
  }
  const noSpecialChars = /^[^`~!@#$%^&*()_+={}\[\]|\\:;“’<,>.?๐฿]*$/.test(val);
  const hasNum = /^.*[0-9].*/.test(val);
  if (!noSpecialChars || hasNum) {
    return true;
  } else {
    return false;
  }
}

/**
 * If any testStr is a member of str, returns true. Otherwise, false.
 * @param str string to test membership in
 * @param testStrs strings whose membership is being tested
 */
export function areMembers(str: string, testStrs: string[]): boolean {
  if (str == null || testStrs == null) {
    return false;
  }
  return testStrs
    .map((testStr) => str.indexOf(testStr) !== -1)
    .reduce((curr, acc) => curr || acc, false);
}

export function isNumber(value: number | string): boolean {
  return (typeof value === "string" &&
    value.trim() &&
    value !== "\n" &&
    !isNaN(+value)) ||
    (typeof value === "number" && !isNaN(value))
    ? true
    : false;
}

/**
 * Applies a fade to the text inside of the domNode if the domNode's parentElement
 * becomes too small to fully display the domNode's text.
 *
 * Assumes that the domNode contains only the textNode that the fade will be applied to,
 * and no other children.
 *
 * Also assumes that the domNode is the last child of the domNode's parent. This assumption is
 * made because when changing the width of the parentElement, it would only make sense to
 * apply the fade to the right most element in the parentElement, since Element is the
 * one that would become cut off from a width change
 * @param domNode An HTMLElement which contains the text node that the blur will be applied to
 */
export function applyTextFade(domNode: HTMLElement) {
  if (
    false ||
    (document as IDocument).documentMode ||
    !!(window as any).StyleMedia
  ) {
    return;
  }
  const parentElem = domNode.parentElement;
  // Necessary Parent styles to apply
  parentElem.style.position = "relative";
  parentElem.style.overflowX = "hidden";
  parentElem.style.whiteSpace = "nowrap";
  parentElem.style.width = "100%";

  // setTimeout is necessary, because without it the first calculation happens immediately
  // when the right positions aren't accurate yet
  setTimeout(() => {
    resizeObserver.observe(parentElem);
  });
}

const resizeObserver =
  false ||
  !!(document as IDocument).documentMode ||
  !!(window as any).StyleMedia
    ? null
    : new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const childToApplyBlur: Element =
            entry.target.children[entry.target.children.length - 1];

          const boundedRect = childToApplyBlur.getBoundingClientRect();
          const childRightCoord = boundedRect.width;
          const parentRightCoord = entry.contentRect.right + 1; // 1 for adjustment

          if (parentRightCoord < childRightCoord) {
            // apply fade
            childToApplyBlur.classList.add("esri-instant-gallery__text-fade");
          } else {
            // remove fade
            childToApplyBlur.classList.remove(
              "esri-instant-gallery__text-fade"
            );
          }
        });
      });

export function applyShadowStyle(newStyle: string, domElem: HTMLElement): void {
  const style = document.createElement("style");
  style.innerHTML = newStyle;
  domElem?.shadowRoot?.appendChild(style);
}

export function decideScreenshotUrl(
  item: __esri.PortalItem,
  templateType: EAppTemplateType,
  portal: __esri.Portal
): string {
  let finalScreenshotUrl = item?.screenshots?.[0];
  return finalScreenshotUrl != null
    ? `${getBaseUrl(portal)}/sharing/rest/content/items/${
        item.id
      }/info/${finalScreenshotUrl}`
    : item.thumbnailUrl; // fall back to thumbnail
}

/**
 * For safari context stack rendering issue:
 * https://devtopia.esri.com/WebGIS/arcgis-template-landing/issues/635
 */
export function prepRootModal() {
  if (!document.getElementById("rootModal")) {
    const rootModal = document.createElement("div");
    rootModal.id = "rootModal";
    document.body.prepend(rootModal);
  }
}
